import React, { useState, useEffect } from 'react';
import { FaMoneyBillWave, FaSearch, FaEdit, FaFilePdf, FaCogs, FaUpload, FaFileAlt, FaSpinner, FaPlus } from 'react-icons/fa';
import { Table } from 'react-bootstrap';
import planillaEmpleadosApi from '../../services/api/planillaEmpleadosApi';
import PlanillaModal from './modals/PlanillaModal';
import CargaMasivaPlanilla from './modals/CargaMasivaPlanilla';
import ActualizacionMasivaPlanilla from './modals/ActualizacionMasivaPlanilla';
import * as XLSX from 'xlsx';
import pythonApiClient from '../../services/api/pythonBaseApi';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

const getProjectId = () => {
  try {
    const p = JSON.parse(localStorage.getItem('selectedProject') || 'null');
    return p?.id || null;
  } catch { return null; }
};

const Planilla = () => {
  const proyectoId = getProjectId();
  const currentDate = new Date();
  const [anio, setAnio] = useState(currentDate.getFullYear());
  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [search, setSearch] = useState('');
  
  const [planillas, setPlanillas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [showCargaMasivaModal, setShowCargaMasivaModal] = useState(false);
  const [showActualizacionMasivaModal, setShowActualizacionMasivaModal] = useState(false);

  useEffect(() => {
    const fetchPlanillas = async () => {
      try {
        setLoading(true);
        const res = await planillaEmpleadosApi.getPlanillasPorPeriodo(anio, mes, search, proyectoId);
        setPlanillas(res.data);
      } catch (error) {
        console.error("Error al cargar planilla:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const t = setTimeout(() => fetchPlanillas(), 400);
    return () => clearTimeout(t);
  }, [anio, mes, search, proyectoId, refreshTrigger]);

  const downloadReport = async () => {
    try {
      const res = await pythonApiClient.get(`/rrhh/planilla-empleados/registradas-masiva`, {
        params: { anio, mes, proyecto_id: proyectoId }
      });
      const registradas = res.data;

      if (!registradas || registradas.length === 0) {
        alert("No hay planillas registradas para este período.");
        return;
      }

      const data = registradas.map(emp => ({
        'DOCUMENTO':                   emp.numero_documento_identidad || '',
        'NOMBRE':                      emp.nombre_completo || '',
        'DÍAS TRABAJADOS':             emp.dias_trabajados || 0,
        'SUELDO CONGELADO':            emp.sueldo_congelado || 0,
        'REMUNERACIÓN':                emp.remuneracion || 0,
        'ASIGNACIÓN FAMILIAR':         emp.asignacion_familiar || 0,
        'HHEE 25% (Cantidad)':         emp.hhee_25_cantidad || 0,
        'MONTO 25%':                   emp.hhee_25_monto || 0,
        'HHEE 33% (Cantidad)':         emp.hhee_33_cantidad || 0,
        'MONTO 33%':                   emp.hhee_33_monto || 0,
        'FERIADO TRABAJADO (Días)':    emp.feriado_trabajado_dias || 0,
        'FERIADO (Monto)':             emp.feriado_trabajado_monto || 0,
        'DESCANSO VENDIDO (Días)':     emp.descanso_vendido_dias || 0,
        'DESCANSO (Monto)':            emp.descanso_vendido_monto || 0,
        'BONO FIJO':                   emp.bono_fijo || 0,
        'OBS. BONO FIJO':              emp.bono_fijo_observacion || '',
        'MOVILIDAD':                   emp.movilidad_monto || 0,
        'SUELDO BRUTO':                emp.sueldo_bruto || 0,
        'ONP/AFP':                     emp.fondo_pensiones || '',
        'AFP':                         emp.codigo_afp || '',
        'TIPO COMISIÓN':               emp.tipos_comision_codigo || '',
        'PORC. APORTE':                emp.porc_aporte_afp_onp || 0,
        'DESC. APORTE':                emp.desc_aporte_afp_onp || 0,
        'PORC. SEGURO':                emp.porc_prima_seguro_comision || 0,
        'DESC. SEGURO':                emp.desc_prima_seguro_comision || 0,
        'RENTA 5TA CATEGORÍA':         emp.desc_quinta_categoria || 0,
        'ADELANTOS':                   emp.desc_adelanto || 0,
        'BANCO':                       emp.banco_codigo || '',
        'CUENTA':                      emp.numero_cuenta || '',
        'CCI':                         emp.cci || '',
        'SUELDO NETO':                 emp.sueldo_neto || 0,
        'DEPÓSITO SUELDO':             emp.deposito_sueldo || 0,
        'DEPÓSITO BONO+DESC':          emp.deposito_bono_descanso || 0,
        'DEPÓSITO MOVILIDAD':          emp.deposito_movilidad || 0,
        'DEPÓSITO TOTAL':              emp.deposito_total || 0
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = Object.keys(data[0]).map(key => {
        let maxLen = key.length;
        data.forEach(row => {
            const val = String(row[key]);
            if (val.length > maxLen) maxLen = val.length;
        });
        return { wch: Math.min(Math.max(maxLen + 2, 12), 50) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reporte Planilla");
      XLSX.writeFile(wb, `Reporte_Planilla_${mes}_${anio}.xlsx`);
    } catch (err) {
      console.error(err);
      alert("Error al obtener el reporte de planillas registradas.");
    }
  };

  const meses = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  const anios = [2023, 2024, 2025, 2026, 2027, 2028, 2029];

  return (
    <div className="container-fluid p-4" style={{ fontFamily: "'Inter', sans-serif" }}>
      
      {/* Tarjeta */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{
            background: 'linear-gradient(135deg,#667eea,#764ba2)',
            color: '#fff', padding: '1.5rem 2rem', border: 'none',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12
        }}>
            <h5 style={{ color: '#fff', fontWeight: 700, fontSize: '1.5rem', margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
                <FaMoneyBillWave size={28} />
                Planilla de Empleados
            </h5>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label style={{ fontWeight: 600, fontSize: 14 }}>Período:</label>
                <select value={mes} onChange={e => setMes(Number(e.target.value))}
                    style={{ padding: '6px 12px', borderRadius: 8, border: 'none', outline: 'none', fontSize: 14, color: '#374151', fontWeight: 500 }}>
                    {meses.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <select value={anio} onChange={e => setAnio(Number(e.target.value))}
                    style={{ padding: '6px 12px', borderRadius: 8, border: 'none', outline: 'none', fontSize: 14, color: '#374151', fontWeight: 500 }}>
                    {anios.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
            </div>
        </div>

        {/* Acciones principales */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button
                    onClick={async () => {
                        try {
                            setLoading(true);
                            const res = await pythonApiClient.post('/rrhh/planilla-empleados/generacion-masiva-ideal', {
                                anio, mes, proyecto_id: proyectoId
                            });
                            toast.success(res.data.message || "Planillas generadas exitosamente.");
                            setRefreshTrigger(prev => prev + 1);
                        } catch (err) {
                            toast.error(err.response?.data?.detail || err.response?.data?.message || "Error al generar planillas");
                        } finally {
                            setLoading(false);
                        }
                    }}
                    disabled={loading}
                    onMouseEnter={e => { e.currentTarget.style.transform = loading ? 'none' : 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                    style={{
                        background: 'linear-gradient(135deg,#11998e,#38ef7d)',
                        border: 'none', color: '#fff', borderRadius: 10, padding: '10px 16px',
                        fontWeight: 600, boxShadow: '0 4px 14px rgba(17,153,142,.3)',
                        transition: 'all .3s', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
                        opacity: loading ? 0.7 : 1
                    }}>
                    <FaCogs size={16} /> {loading ? 'Generando...' : 'Generar Planilla Automática'}
                </button>
                <button
                    onClick={() => setShowCargaMasivaModal(true)}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                    style={{
                        background: 'linear-gradient(135deg,#f5af19,#f12711)',
                        border: 'none', color: '#fff', borderRadius: 10, padding: '10px 16px',
                        fontWeight: 600, boxShadow: '0 4px 14px rgba(241,39,17,.3)',
                        transition: 'all .3s', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14
                    }}>
                    <FaUpload size={16} /> Carga Planilla Masiva
                </button>
                <button
                    onClick={() => setShowActualizacionMasivaModal(true)}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                    style={{
                        background: 'linear-gradient(135deg,#ff9966,#ff5e62)',
                        border: 'none', color: '#fff', borderRadius: 10, padding: '10px 16px',
                        fontWeight: 600, boxShadow: '0 4px 14px rgba(255,94,98,.3)',
                        transition: 'all .3s', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14
                    }}>
                    <FaEdit size={16} /> Actualizar Planilla Masiva
                </button>
            </div>
            
            <div style={{ display: 'flex', gap: 12 }}>
                <button
                    onClick={downloadReport}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
                    style={{
                        background: 'linear-gradient(135deg,#36D1DC,#5B86E5)',
                        border: 'none', color: '#fff', borderRadius: 10, padding: '10px 16px',
                        fontWeight: 600, boxShadow: '0 4px 14px rgba(91,134,229,.3)',
                        transition: 'all .3s', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14
                    }}>
                    <FaFileAlt size={16} /> Reporte Planilla Mensual
                </button>
            </div>
        </div>

        {/* Barra búsqueda */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
            <FaSearch style={{ position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />
            <input
              type="text" placeholder="Buscar por nombre o número de documento…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10,
                border: '1.5px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="table-responsive" style={{ padding: '0 24px 24px' }}>
          <Table hover style={{ marginBottom: 0, marginTop: 16 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ width: '12%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Documento</th>
                <th style={{ width: '18%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Nombre</th>
                <th style={{ width: '12%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Dep. Sueldo</th>
                <th style={{ width: '12%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Dep. Bono/Feriado/Desc.</th>
                <th style={{ width: '12%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Dep. Movilidad</th>
                <th style={{ width: '12%', color: '#166534', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Dep. Total</th>
                <th style={{ width: '12%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Fecha/Act.</th>
                <th style={{ width: '10%', color: '#4b5563', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', padding: '14px 16px', border: 'none', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
                {loading ? (
                    <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                            <FaSpinner className="fa-spin" size={24} style={{ marginBottom: 10, color: '#667eea' }} />
                            <div style={{ fontSize: 14 }}>Cargando empleados...</div>
                        </td>
                    </tr>
                ) : planillas.length === 0 ? (
                    <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#6b7280', fontSize: 14 }}>
                            No se encontraron empleados para los filtros seleccionados.
                        </td>
                    </tr>
                ) : (
                    planillas.map(item => (
                        <tr key={item.empleado_id} style={{ borderBottom: '1px solid #f3f4f6', background: item.estado === 'pendiente' ? '#fffbeb' : 'transparent' }}>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14 }}>{item.numero_documento_identidad}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'left', verticalAlign: 'middle', fontSize: 14, fontWeight: 500 }}>
                                {item.nombre_completo}
                                {item.estado === 'pendiente' && (
                                    <span style={{ marginLeft: 8, fontSize: 10, background: '#f59e0b', color: '#fff', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                                        NUEVO
                                    </span>
                                )}
                            </td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14 }}>{item.deposito_sueldo}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14 }}>{item.deposito_bono_descanso}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14 }}>{item.deposito_movilidad}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14, fontWeight: 700, color: '#166534', background: '#ecfccb' }}>{item.deposito_total}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle', fontSize: 14, color: '#6b7280' }}>{item.fecha_act}</td>
                            <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    <button 
                                        onClick={() => { setSelectedData(item); setShowModal(true); }}
                                        style={{ 
                                            background: 'linear-gradient(135deg,#667eea,#764ba2)',
                                            color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px',
                                            cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 
                                        }}
                                    >
                                        {item.estado === 'pendiente' ? <><FaPlus /> Generar</> : <><FaEdit /> Editar</>}
                                    </button>
                                    {item.estado !== 'pendiente' && (
                                        <button 
                                            onClick={() => alert("Generación PDF aún en DESARROLLO")}
                                            style={{ 
                                                background: '#dc2626',
                                                color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px',
                                                cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 
                                            }}
                                            title="Generar PDF"
                                        >
                                            <FaFilePdf />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
          </Table>
        </div>
      </div>
      
      {showModal && (
        <PlanillaModal
          show={showModal}
          onHide={() => { setShowModal(false); setSelectedData(null); }}
          onSaved={() => {
              setShowModal(false);
              setSelectedData(null);
              setRefreshTrigger(prev => prev + 1);
          }}
          data={selectedData}
          anio={anio}
          mes={mes}
          proyectoId={proyectoId}
        />
      )}

      <CargaMasivaPlanilla
        show={showCargaMasivaModal}
        onClose={() => setShowCargaMasivaModal(false)}
        onSaved={() => {
            setShowCargaMasivaModal(false);
            setRefreshTrigger(prev => prev + 1);
        }}
        anio={anio}
        mes={mes}
        proyectoId={proyectoId}
      />

      <ActualizacionMasivaPlanilla
        show={showActualizacionMasivaModal}
        onHide={() => setShowActualizacionMasivaModal(false)}
        onExito={() => {
            setShowActualizacionMasivaModal(false);
            setRefreshTrigger(prev => prev + 1);
        }}
        anio={anio}
        mes={mes}
        proyectoId={proyectoId}
      />
      <ToastContainer />
    </div>
  );
};

export default Planilla;
